import React from 'react';
import { Typography, Box, Paper, Divider, Grid } from '@mui/material';

const PrizesTable = ({ prizes }) => {
    if (!prizes) return null;

    // Obsługa zarówno starszego formatu (obiekt) jak i nowego (tablica obiektów)  
    const isLegacyFormat = typeof prizes === 'object' && !Array.isArray(prizes);

    // Wyodrębniamy dodatkowy opis, jeśli istnieje
    const additionalDescription = isLegacyFormat
        ? prizes["Dodatkowy opis"]
        : prizes.find(prize => prize.position === "Dodatkowy opis")?.description;

    // Sprawdzamy, czy są jakieś inne nagrody oprócz "Dodatkowy opis"
    const hasOtherPrizes = isLegacyFormat
        ? Object.keys(prizes).some(key => key !== "Dodatkowy opis")
        : prizes.some(prize => prize.position !== "Dodatkowy opis");

    // Przekształcenie starszego formatu do nowego formatu tablicowego
    const prizesArray = isLegacyFormat
        ? Object.entries(prizes)
            .filter(([place]) => place !== "Dodatkowy opis")
            .map(([place, reward], index) => ({ place, reward, order: index }))
        : prizes
            .filter(prize => prize.position !== "Dodatkowy opis")
            .map(prize => ({
                place: prize.position,
                reward: prize.description,
                order: prize.order || 0
            }));

    // Sortowanie nagród według kolejności
    const sortedPrizes = [...prizesArray].sort((a, b) => (a.order || 0) - (b.order || 0));

    // Jeśli nie ma innych nagród, wyświetlamy tylko dodatkowy opis
    if (!hasOtherPrizes && additionalDescription) {
        return (
            <Paper
                sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: '0.5rem',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography
                        sx={{
                            color: 'white',
                            whiteSpace: 'pre-line',
                            fontSize: '0.85rem',
                            lineHeight: '1.5'
                        }}
                    >
                        {additionalDescription}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    // Funkcja renderująca pojedynczy element nagrody
    const renderPrizeItem = (prizeItem) => {
        const place = prizeItem.place;
        const reward = prizeItem.reward;

        return (
            <Box
                sx={{
                    p: { xs: 1, sm: 1.5 },
                    height: '100%',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(76, 29, 149, 0.05)'
                    },
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start'
                }}>
                    <Box
                        component="span"
                        sx={{
                            backgroundColor: 'rgba(76, 29, 149, 0.4)',
                            borderRadius: '4px',
                            px: 1,
                            py: 0.25,
                            mr: 1.5,
                            fontSize: '0.7rem',
                            minWidth: '28px',
                            textAlign: 'center',
                            color: 'rgb(196, 181, 253)',
                            fontWeight: 'bold',
                            flexShrink: 0
                        }}
                    >
                        {place}
                    </Box>

                    <Typography
                        sx={{
                            color: 'white',
                            whiteSpace: 'pre-line',
                            fontSize: '0.85rem',
                            lineHeight: '1.4',
                            flex: 1
                        }}
                    >
                        {Array.isArray(reward) ? reward.join('\n') : reward}
                    </Typography>
                </Box>
            </Box>
        );
    };

    // Tworzenie par nagród (dla układu poziomego)
    const rows = [];
    for (let i = 0; i < sortedPrizes.length; i += 2) {
        const row = {
            left: sortedPrizes[i],
            right: i + 1 < sortedPrizes.length ? sortedPrizes[i + 1] : null
        };
        rows.push(row);
    }

    return (
        <Paper
            sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                borderRadius: '0.5rem',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                overflow: 'hidden'
            }}
        >
            {/* Lista nagród - układ poziomy */}
            <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
                {/* Na urządzeniach mobilnych: jedna kolumna */}
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                    {sortedPrizes.map((prize, index) => (
                        <Box
                            key={prize.place || index}
                            sx={{
                                borderBottom: index < sortedPrizes.length - 1 ? '1px solid rgba(76, 29, 149, 0.15)' : 'none'
                            }}
                        >
                            {renderPrizeItem(prize)}
                        </Box>
                    ))}
                </Box>

                {/* Na większych ekranach: układ dwukolumnowy poziomy */}
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    {rows.map((row, rowIndex) => (
                        <Grid
                            container
                            key={`row-${rowIndex}`}
                            sx={{
                                borderBottom: rowIndex < rows.length - 1 ? '1px solid rgba(76, 29, 149, 0.15)' : 'none'
                            }}
                        >
                            {/* Lewa kolumna */}
                            <Grid item xs={6} sx={{
                                borderRight: row.right ? '1px solid rgba(76, 29, 149, 0.15)' : 'none'
                            }}>
                                {renderPrizeItem(row.left)}
                            </Grid>

                            {/* Prawa kolumna (jeśli istnieje) */}
                            {row.right && (
                                <Grid item xs={6}>
                                    {renderPrizeItem(row.right)}
                                </Grid>
                            )}
                        </Grid>
                    ))}
                </Box>
            </Box>

            {/* Sekcja dodatkowych informacji */}
            {additionalDescription && hasOtherPrizes && (
                <>
                    <Divider sx={{ backgroundColor: 'rgba(76, 29, 149, 0.15)' }} />
                    <Box sx={{ p: 2 }}>
                        <Typography
                            sx={{
                                color: 'white',
                                whiteSpace: 'pre-line',
                                fontSize: '0.85rem',
                                lineHeight: '1.5'
                            }}
                        >
                            <Box component="span" sx={{ color: 'rgb(196, 181, 253)', fontWeight: 'bold' }}>
                                Dodatkowo:
                            </Box>
                            <br />
                            {additionalDescription}
                        </Typography>
                    </Box>
                </>
            )}
        </Paper>
    );
}

export default PrizesTable;